import React, { useEffect, useState, useContext, forwardRef, useMemo } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import CalculationLinkBox from './CalculationLinkBox';
import SearchBoxHomePage from './SearchBoxHomePage';
import { convertFirstCharacterAllWordsToUppercase } from '../../../helpers/convertStringToUpperLowerCase';
import { calculationsData, homepageCategories } from './calculationBoxData';
import { Context } from '../../../App';
import SeoHelmet from '../../common/SeoHelmet';
import './HomePage.css';

const CATEGORY_ORDER = homepageCategories.map((category) => category.key);
const CATEGORY_LABELS = homepageCategories.reduce((acc, category) => ({ ...acc, [category.key]: category.label }), {});
const DEFAULT_CATEGORY_KEY = CATEGORY_ORDER[CATEGORY_ORDER.length - 1] || 'siteCharacterization';

const HomePage = () => {
  const { user, lastpage, setLastpage } = useContext(Context);
  const [filteredCalculations, setFilteredCalculations] = useState([]);
  const [matchingResult, setMatchingResult] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  //Sncakbar
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={0} ref={ref} variant="standard" {...props} />;
  });
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  //Handle welcome message for the user
  useEffect(() => {
    if (user && (lastpage === './login' || lastpage === './signup')) {
      setAlertMessage(`Welcome ${convertFirstCharacterAllWordsToUppercase(user.name)}. Happy calculating :)`);
      setOpen(true);
    }
    setLastpage('');
  }, [user, lastpage, setAlertMessage, setLastpage, setOpen]);

  //Handle favorites
  const [favorites, setFavorites] = useState([]);
  const [checked, setChecked] = useState(false);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  const handleFavorite = (e, id) => {
    if (e.target.tagName === 'svg' || e.target.tagName === 'path') e.preventDefault();

    if (!favorites.includes(id) && id !== -1) {
      setFavorites((favorites) => [...favorites, id]);
    } else if (id !== -1) {
      setFavorites(favorites.filter((el) => el !== id));
    }
  };

  const handleChange = () => {
    setChecked(!checked);
  };

  useEffect(() => {
    if (!user) return;

    let source;
    if (source) source.cancel();
    source = axios.CancelToken.source();

    const getFavoriteCalculations = async () => {
      setLoadingFavorites(true);
      try {
        const response = await axios.get('/api/v1/calculations/getFavorites', {
          cancelToken: source.token,
        });

        setFavorites(response.data.data.favoriteCalculations.calculations);
        setChecked(response.data.data.favoriteCalculations.show);
      } catch (error) {
        if (axios.isCancel(error)) {
          /* console.log('error getFavoriteCalculations', error); */
        } else {
          throw error;
        }
      }
      setLoadingFavorites(false);
      setFavoritesLoaded(true);
    };
    getFavoriteCalculations();
    return () => {
      source.cancel();
    };
  }, [user]);

  useEffect(() => {
    if (!user || !favoritesLoaded) return;
    let source;
    if (source) source.cancel();
    source = axios.CancelToken.source();

    const setFavoriteCalculations = async () => {
      try {
        await axios({
          method: 'POST',
          url: '/api/v1/calculations/setFavorites',
          cancelToken: source.token,
          data: { calculations: favorites, show: checked },
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          /* console.log('error setFavoriteCalculations', error); */
        } else {
          throw error;
        }
      }
    };
    setFavoriteCalculations();
    return () => {
      source.cancel();
    };
  }, [favorites, checked, user, favoritesLoaded]);

  const [favoritesMatchingResult, setFavoritesMatchingResult] = useState();
  useEffect(() => {
    const test = filteredCalculations.filter((el) => {
      return favorites.includes(el.id);
    });

    if (test.length > 0) {
      setFavoritesMatchingResult(false);
    } else {
      setFavoritesMatchingResult(true);
    }
    if (favorites.length === 0) setChecked(false);
  }, [filteredCalculations, favorites]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (query.trim()) {
      nextParams.set('q', query.trim());
    }
    setSearchParams(nextParams, { replace: true });
  }, [query, setSearchParams]);

  const displayedCalculations = useMemo(() => {
    if (!checked) {
      return filteredCalculations.filter((el) => el.id !== -1);
    }
    return filteredCalculations.filter((el) => favorites.includes(el.id));
  }, [checked, favorites, filteredCalculations]);

  const groupedCalculations = useMemo(() => {
    const groups = CATEGORY_ORDER.reduce((acc, key) => ({ ...acc, [key]: [] }), {});
    displayedCalculations.forEach((el) => {
      const category = groups[el.category] ? el.category : DEFAULT_CATEGORY_KEY;
      groups[category].push(el);
    });
    return groups;
  }, [displayedCalculations]);

  useEffect(() => {
    const ac = new AbortController();
    const loadMetrics = async () => {
      setMetricsLoading(true);
      try {
        const response = await axios.get('/api/v1/users/getAboutMetrics', {
          signal: ac.signal,
          headers: { Accept: 'application/json' },
        });
        setMetrics(response.data);
      } catch (error) {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          setMetrics(null);
        }
      } finally {
        setMetricsLoading(false);
      }
    };

    loadMetrics();
    return () => ac.abort();
  }, []);

  const formatCount = (value) => {
    if (typeof value !== 'number') return '...';
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const formatFullCount = (value) => {
    if (typeof value !== 'number') return '';
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Home"
        description="Professional geotechnical calculation tools for engineers: bearing capacity, USCS classification, SPT corrections, settlement, and more — all in one place."
        url="https://www.geocalc.app/"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaExtra={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'GeoCalc',
            url: 'https://www.geocalc.app',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.geocalc.app/?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'GeoCalc',
            url: 'https://www.geocalc.app',
            logo: 'https://www.geocalc.app/assets/geoCalcLogo.png',
            sameAs: ['https://www.linkedin.com/in/geotechnicalcalculations/', 'https://x.com/geocalculations'],
          },
        ]}
      />
      <Header />
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity="info" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      {user && user?.emailConfirmed === false && (
        <Alert severity="warning">
          <AlertTitle>
            <strong>Confirm your email</strong>
          </AlertTitle>
          <span>
            You have to confirm your email to use the calculation tools below. We have already sent you an email about this. If
            the email did not reach you or 24 hours passed after you got the email, you can resend it from your{' '}
            <Link to={'/account'}>
              <span>'Account'</span>
            </Link>{' '}
            page.
          </span>
        </Alert>
      )}
      <main className={`${loadingFavorites ? 'homepage-loading' : 'homepage-modern'}`}>
        {!loadingFavorites && (
          <section className="homepage-hero">
            <div className="homepage-hero-inner">
              <p className="homepage-eyebrow">Geotechnical Engineering Toolkit</p>
              <h1>Engineering-Grade Geotechnical Tools for Daily Design Work</h1>
              <p className="homepage-subtitle">
                Search and run practical geotechnical calculations with references, standards, and theory pages in one workflow.
              </p>
            </div>
            <div className="homepage-hero-stats">
              <div className="stat-card">
                <span className="stat-value" title={formatFullCount(metrics?.users)}>
                  {metricsLoading ? '...' : formatCount(metrics?.users)}
                </span>
                <span className="stat-label">Users</span>
              </div>
              <div className="stat-card">
                <span className="stat-value" title={formatFullCount(metrics?.calculations)}>
                  {metricsLoading ? '...' : formatCount(metrics?.calculations)}
                </span>
                <span className="stat-label">Calculations Performed</span>
              </div>
              <div className="stat-card">
                <span className="stat-value" title={formatFullCount(metrics?.tools ?? calculationsData.length)}>
                  {metricsLoading ? '...' : formatCount(metrics?.tools ?? calculationsData.length)}
                </span>
                <span className="stat-label">Calculation Tools</span>
              </div>
              <div className="stat-card">
                <span className="stat-value" title={formatFullCount(metrics?.updates)}>
                  {metricsLoading ? '...' : formatCount(metrics?.updates)}
                </span>
                <span className="stat-label">Updates Shipped</span>
              </div>
              <p className="homepage-hero-asof">As of {metricsLoading ? 'updating...' : metrics?.asOfISO || 'latest update'}</p>
            </div>
          </section>
        )}

        {!loadingFavorites && (
          <section className="homepage-search-panel">
            <SearchBoxHomePage
              calculationsData={calculationsData}
              query={query}
              setQuery={setQuery}
              setFilteredCalculations={setFilteredCalculations}
              setMatchingResult={setMatchingResult}
            />
            {user && <FormControlLabel control={<Switch checked={checked} onChange={handleChange} />} label="Show Favorites" />}
          </section>
        )}

        {loadingFavorites && (
          <div className="homepage-loading-spinner">
            <CircularProgress />
          </div>
        )}

        {!loadingFavorites && (
          <section className="homepage-links-container">
            {CATEGORY_ORDER.map((categoryKey) => {
              const items = groupedCalculations[categoryKey];
              if (!items || items.length === 0) return null;

              return (
                <section className="homepage-category-section" key={categoryKey}>
                  <div className="homepage-category-header">
                    <h2>{CATEGORY_LABELS[categoryKey]}</h2>
                  </div>
                  <div className="homepage-category-grid">
                    {items.map((el) => (
                      <CalculationLinkBox
                        key={`${el.title} - ${el.description}`}
                        el={el}
                        handleFavorite={handleFavorite}
                        favorites={favorites}
                        user={user}
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            {!checked && matchingResult && <div className="no-match-warning">No matching result for your query.</div>}
            {checked && favoritesMatchingResult && (
              <div className="no-match-warning">You have no favorite calculations for your query.</div>
            )}
          </section>
        )}
      </main>

      <Footer />
    </>
  );
};

export default HomePage;
